import { useEffect, useState } from 'react';
import { fetchSchedulesOnce, fetchSessionsOnce } from '../../../../../components/psychologist/calendar/data';
import { timeToMinutes } from '../../../../../components/psychologist/calendar/utils';

export interface RescheduleSlot {
  date: string;
  dayLabel: string;
  startTime: string;
  endTime: string;
}

const DAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const ACTIVE_STATUSES = new Set(['active', 'settled', 'scheduled', 'incomplete']);
const REQUIRED_MINUTES = 240; // 4h

/**
 * Versión liviana e independiente de la grilla semanal del cálculo de "horas
 * disponibles en los próximos 7 días" que alimenta el banner 1 (reprogramación),
 * para poder mostrarlo también en diaria y mensual (WeeklyPsychologistCalendar no
 * se monta en esas vistas). A diferencia del cálculo de esa grilla, solo considera
 * el horario fijo recurrente — no los overrides puntuales de un día específico —
 * para no duplicar ese fetch acá; el detalle exacto sigue viéndose bien en semanal.
 */
export function useRescheduleAvailabilityBanner(psychologistId: string | undefined) {
  const [rescheduleMinutes, setRescheduleMinutes] = useState(0);
  const [rescheduleSlotsNextWeek, setRescheduleSlotsNextWeek] = useState<RescheduleSlot[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!psychologistId) return;
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
        const weekKey = `reschedule-banner-${tomorrow.toISOString().slice(0, 10)}_${psychologistId}`;

        const [template, sessions] = await Promise.all([
          fetchSchedulesOnce(psychologistId),
          fetchSessionsOnce(psychologistId, tomorrow, weekEnd, weekKey, 'general'),
        ]);
        if (cancelled) return;

        let totalMinutes = 0;
        const slots: RescheduleSlot[] = [];
        const blocksByDate = new Map<string, Array<{ start: number; end: number }>>();

        for (let i = 1; i <= 7; i++) {
          const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
          const appWeekday = (date.getDay() + 6) % 7; // 0=Mon..6=Sun
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const dayLabel = `${DAYS_ES[date.getDay()]} ${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
          const blocks: Array<{ start: number; end: number }> = [];
          for (const t of template) {
            if (!t.active || t.weekday !== appWeekday) continue;
            const start = timeToMinutes(t.startTime);
            let end = timeToMinutes(t.endTime);
            if (end === 0) end = 1440;
            totalMinutes += end - start;
            slots.push({ date: dateStr, dayLabel, startTime: t.startTime, endTime: t.endTime });
            blocks.push({ start, end });
          }
          blocksByDate.set(dateStr, blocks);
        }

        let reserved = 0;
        blocksByDate.forEach((blocks, dateStr) => {
          if (blocks.length === 0) return;
          for (const session of sessions) {
            if (session.date !== dateStr || !ACTIVE_STATUSES.has(session.status)) continue;
            const sessionStart = timeToMinutes(session.time);
            if (blocks.some((b: { start: number; end: number }) => sessionStart >= b.start && sessionStart < b.end)) {
              reserved += session.duration ?? 60;
            }
          }
        });

        setRescheduleMinutes(Math.max(0, totalMinutes - reserved));
        setRescheduleSlotsNextWeek(slots);
        setReady(true);
      } catch {
        // Ante error, no mostrar el banner (fail-safe)
      }
    })();
    return () => { cancelled = true; };
  }, [psychologistId]);

  const bannerShouldShow = ready && rescheduleMinutes < REQUIRED_MINUTES;

  return { rescheduleMinutes, rescheduleSlotsNextWeek, bannerShouldShow };
}
